import type { Ctx } from '@milkdown/kit/ctx';
import type { useNodeViewFactory } from '@prosemirror-adapter/react';
import { useNodeViewContext } from '@prosemirror-adapter/react';
import { prosePluginsCtx } from '@milkdown/kit/core';
import { Plugin } from '@milkdown/kit/prose/state';
import { BrainfishColors } from '@brainfish-ai/components';
import { CSSProperties, useMemo } from 'react';
import { Info, Warning, Star } from '@phosphor-icons/react';

import { NOTICE_STYLES, type NoticeStyle } from './notice';

const BLOCK_STYLES: Record<NoticeStyle, { backgroundColor: string; borderColor: string; iconColor: string }> = {
  info: {
    backgroundColor: BrainfishColors.lime[100],
    borderColor: BrainfishColors.lime[500],
    iconColor: BrainfishColors.lime[500],
  },
  warning: {
    backgroundColor: BrainfishColors.red[100],
    borderColor: BrainfishColors.red[500],
    iconColor: BrainfishColors.red[500],
  },
  tip: {
    backgroundColor: BrainfishColors.yellow[100],
    borderColor: BrainfishColors.yellow[500],
    iconColor: BrainfishColors.yellow[500],
  },
};

/** Collab/platform can persist unknown styles (e.g. `note`); coerce to a known one. */
function resolveNoticeStyle(raw: unknown): NoticeStyle {
  return NOTICE_STYLES.includes(raw as NoticeStyle) ? (raw as NoticeStyle) : 'info';
}

/**
 * React component for rendering notice blocks in the editor
 */
export const NoticeNodeView = () => {
  const { node, contentRef } = useNodeViewContext();
  const style = resolveNoticeStyle(node.attrs.style);
  const blockStyles: CSSProperties = {
    backgroundColor: BLOCK_STYLES[style].backgroundColor,
    borderColor: BLOCK_STYLES[style].borderColor,
  };
  const Icon = useMemo(() => {
    switch (style) {
      case 'warning':
        return Warning;
      case 'tip':
        return Star;
      case 'info':
      default:
        return Info;
    }
  }, [style]);

  return (
    <div className="flex gap-3 my-2 p-2 pr-4 rounded-lg border-l-4! text-dark" style={blockStyles} data-notice-block-type={style}>
      <Icon size={20} weight="fill" fill={BLOCK_STYLES[style].iconColor} className="shrink-0 !mt-1.5" />
      <div className="flex-1 min-w-0 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0" ref={contentRef} />
    </div>
  );
};

/**
 * Configure the notice NodeView with the editor
 */
export function configureNoticeNodeView(ctx: Ctx, nodeViewFactory: ReturnType<typeof useNodeViewFactory>) {
  ctx.update(prosePluginsCtx, (plugins) => [
    ...plugins,
    new Plugin({
      props: {
        nodeViews: {
          container_notice: nodeViewFactory({ component: NoticeNodeView }),
        },
      },
    }),
  ]);
}
